import React from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { InputAdornment, TextField } from '@mui/material';
import { useDebounce } from 'react-use';
import IconButton from '@mui/material/IconButton';
import { grey } from '@mui/material/colors';

interface SearchFieldProps {
  onSearch: ((value: string) => void) | null;
  placeholder?: string;
  // Control over colors to allow for use in other areas
  backgroundColor?: string;
  searchIconColor?: string;
  textColor?: string;
  clearButtonColor?: string;
  /**
   *  allows for search component to know what the search value should be
   *  which allows us to clear the search value from the url query params, as well
   *  as set the search value from the url query params dynamically.
   */
  searchType?: string;
  /**
   * Initial value to populate the search field with.
   * Useful when syncing with external state (e.g., filters from context).
   */
  initialValue?: string;
}

export const SearchField = ({
  onSearch,
  placeholder,
  backgroundColor,
  searchIconColor,
  clearButtonColor,
  textColor,
  searchType,
  initialValue = '',
}: SearchFieldProps) => {
  const [search, setSearch] = React.useState(initialValue);
  const [debouncedVal, setDebouncedVal] = React.useState(initialValue);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Track if we've initialized to prevent calling onSearch with empty string on mount
  const hasInitializedRef = React.useRef(false);
  // Ref to hold the latest onSearch to avoid stale closure issues in effects
  const onSearchRef = React.useRef(onSearch);
  onSearchRef.current = onSearch;

  // handles search from url query params when navigating from other pages
  React.useEffect(() => {
    if (!searchType) return;
    const params = new URLSearchParams(window.location.search);
    const searchValue = params.get(searchType);
    if (searchValue && !search) {
      setSearch(searchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, [searchType]);

  // Sync with external initialValue changes only when the external value is
  // explicitly cleared (e.g. a "Clear All" button). A parent may transform the
  // value before writing it to filters (e.g. zero-padding a claim number); if
  // we synced unconditionally, that transformed value would overwrite what the
  // user is still typing. Clearing is the one case where reflection is wanted.
  React.useEffect(() => {
    if (hasInitializedRef.current && !initialValue && search) {
      setSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reading `search`
    // here would refire this effect on every keystroke; we only care about
    // external clears, which are driven by `initialValue`.
  }, [initialValue]);

  const [, cancel] = useDebounce(
    () => {
      setDebouncedVal?.(search);
    },
    1000,
    [search]
  );

  React.useEffect(() => {
    // Skip the initial mount trigger if there's no initial value
    // This prevents clearing existing filters when navigating between pages
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      // Only call onSearch on mount if there's an initial value
      if (initialValue) {
        onSearchRef.current?.(debouncedVal);
      }
      return cancel;
    }
    onSearchRef.current?.(debouncedVal);
    return cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `onSearchRef` and
    // `hasInitializedRef` are refs (stable identity); `cancel` is technically
    // unstable but only re-triggers the effect, which reads the latest callback
    // via `onSearchRef.current`, so the behaviour is correct.
  }, [debouncedVal, cancel]);

  React.useEffect(() => {
    // Only clear when user explicitly clears (not on mount)
    if (search === '' && hasInitializedRef.current) {
      onSearchRef.current?.('');
    }
  }, [search]);

  const handleSearch = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setSearch(value);
  };

  // clears the search value from the url query params
  const clearQueryParams = () => {
    if (!searchType) return;
    const params = new URLSearchParams(window.location.search);
    if (params.has(searchType)) {
      params.delete(searchType);
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${params}`
      );
    }
  };

  const handleClear = () => {
    setSearch('');
    inputRef.current?.focus();
    clearQueryParams();
  };

  return (
    <TextField
      inputRef={inputRef}
      inputProps={{ 'data-testid': 'search-bar' }}
      placeholder={placeholder}
      variant="standard"
      InputProps={{
        style: {
          width: '18.35rem',
          color: textColor || 'white',
          padding: '0 !important',
        },
        disableUnderline: true,
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon
              sx={{
                color: searchIconColor || 'white',
                my: 0.1,
                fontSize: '1.25rem',
              }}
            />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {search ? (
              <IconButton onClick={handleClear} edge="end">
                <CloseIcon
                  data-testid="clear-button"
                  sx={{
                    color: clearButtonColor || 'white',
                    fontSize: '1.25rem',
                    '&:hover': {
                      backgroundColor: grey[500],
                      borderRadius: '50%',
                    },
                  }}
                />
              </IconButton>
            ) : null}
          </InputAdornment>
        ),
      }}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '3px 12px 3px 6px',
        position: 'static',
        width: '18.25rem',
        height: '30px',
        left: '0px',
        top: '0px',
        background: backgroundColor || '#282D38',
        border: '1px solid #3A404D',
        boxSizing: 'border-box',
        borderRadius: '50px',
      }}
      onChange={handleSearch}
      value={search}
    />
  );
};

export default SearchField;
